import { KEREKITES_HATAR, SAV_ALAP, SAV_MAX, SZORAS_SULY, type BecslesEredmeny, type SzakertoiOk } from '@ertekbecslo/becslo';
import { ERTEK_CIMKE } from '../../../web/src/lib/cimkek.ts';
import { ADAT_CIMKE } from './ingatlanAdatok.ts';

/**
 * Az online ár-sáv levezetése az irodai adatlapra. Tiszta függvények: a mentett `BecslesEredmeny`-ből és az
 * alapterületből újraszámolják a lépéseket. A látogatói felületen ez nem jelenik meg (üzleti döntés), csak az adminban.
 */

export const FORRAS_CIMKE: Record<string, string> = {
  ksh_utca: 'KSH, utcaszintű adat',
  ksh_terulet: 'KSH, területi adat',
  iroda: 'Iroda saját alapára',
};

/** Rövid jellemzőnevek (a kérdések szövege helyett). */
export const SZAKERTOI_MAGYARAZAT: Record<SzakertoiOk, string> = {
  nincs_alapar: 'Erre a helyre és ingatlantípusra nincs elég KSH-adatunk, és az irodának sincs saját alapára.',
  iroda_ar_szukseges: 'Irodánál és ipari ingatlannál csak az iroda saját alapárával számolunk, és ilyen ár nincs megadva.',
  kiugro_meret: 'Az alapterület a szokásos tartományon kívül esik, ezért nem adtunk automatikus árat.',
  tul_szeles_sav: 'A KSH-adatok túl nagy szórást mutattak, ezért a szabályalapú sáv félrevezető lett volna.',
  nincs_index: 'Ehhez a területhez nincs elérhető MNB-lakásárindex, így az adat nem vetíthető a mai szintre.',
  atipikus_ingatlan: 'Atipikus ingatlan (például vályogfalú ház vagy bizonytalan besorolás), ezért a szabályalapú ár félrevezető lenne.',
  motor_hiba: 'A számítás technikai hiba miatt nem futott le, ezért a lead szakértői ágra került.',
};

export interface Lepes {
  fajta: 'alapar' | 'index' | 'szorzo';
  kulcs: string;
  cimke: string;
  reszlet: string;
  /** Szorzó (alapárnál null). */
  szorzo: number | null;
  /** Ft/m² a lépés előtt (alapárnál null) és után, kerekítetlenül. */
  elotte: number | null;
  utana: number;
  valtozasFt: number;
  /** Százalék, előjelesen (+10 = 10%-kal több). Alapárnál 0. */
  valtozasSzazalek: number;
}

export interface NemModosito { cimke: string; ertek: string }

export interface SavLeiras {
  szelesseg: number;
  korlatozva: boolean;
  /** Az indok mondatai, olvasható magyarul. */
  indokok: string[];
}

export interface SavLevezetes {
  tipus: 'sav';
  osszefoglalo: string;
  lepesek: Lepes[];
  nemModosit: NemModosito[];
  /** A szorzatok utáni Ft/m², kerekítetlenül; ez szorzódik az alapterülettel. */
  nyersFtPerM2: number;
  /** A mentett (ezerre kerekített) Ft/m². */
  ftPerM2: number;
  alapterulet: number;
  kozepNyers: number;
  minNyers: number;
  maxNyers: number;
  sav: SavLeiras;
  kerekitesSzoveg: string;
  min: number;
  kozep: number;
  max: number;
}

export interface SzakertoiLevezetes { tipus: 'szakertoi'; osszefoglalo: string; magyarazat: string; ok: string }
export type Levezetes = SavLevezetes | SzakertoiLevezetes;

export function szazalekSzoveg(szazalek: number): string {
  const r = Math.round(szazalek * 10) / 10;
  if (r === 0) return '0%';
  return `${r > 0 ? '+' : '−'}${Math.abs(r).toLocaleString('hu-HU', { maximumFractionDigits: 1 })}%`;
}

export function negyedevSzoveg(negyedev: string): string {
  const m = /^(\d{4})Q([1-4])$/.exec(negyedev);
  return m ? `${m[1]}. ${m[2]}. negyedév` : negyedev;
}

function szam(n: number, tizedes = 4): string {
  return n.toLocaleString('hu-HU', { maximumFractionDigits: tizedes });
}

function ertekCimke(kulcs: string, ertek: string): string {
  return ERTEK_CIMKE[`${kulcs}.${ertek}`] ?? ertek;
}

function savLeiras(e: Extract<BecslesEredmeny, { tipus: 'sav' }>): SavLeiras {
  const indokok: string[] = [];
  const fo = e.forras === 'iroda' ? 'iroda saját alapára' : e.forras === 'ksh_utca' ? 'utcaszintű KSH-adat' : 'területi KSH-adat';
  const pct = (x: number) => `±${szam(x * 100, 1)}%`;
  if (e.savOk) {
    const { szoras, kevesUgyletPotlek } = e.savOk;
    if (szoras !== null && szoras > e.savOk.alap) {
      indokok.push(`A KSH-adásvételi árak szórása nagy (${szam((e.alaparRelativSzoras ?? szoras / SZORAS_SULY) * 100, 0)}%), ezért az alap ${pct(e.savOk.alap)} helyett ${pct(szoras)} a sáv.`);
    } else {
      indokok.push(`Alap sávszélesség: ${pct(e.savOk.alap)} (${fo}).`);
    }
    if (kevesUgyletPotlek > 0) {
      indokok.push(`Kevés az adásvétel (${e.alaparUgyletszam ?? '5-nél kevesebb'} db), ezért +${szam(kevesUgyletPotlek * 100, 0)} százalékpont.`);
    }
  } else {
    const alap = SAV_ALAP[e.forras];
    indokok.push(`Alap sávszélesség: ${pct(alap)} (${fo}).`);
    if (e.savSzelesseg > alap + 1e-9 && !e.savKorlatozva) {
      indokok.push('Ennél szélesebb a sáv, mert a KSH-adatok szórása nagy vagy kevés az adásvétel.');
    }
  }
  if (e.savKorlatozva) indokok.push(`A számolt sáv szélesebb lett volna, ezért ${pct(SAV_MAX)}-nál megállítottuk.`);
  return { szelesseg: e.savSzelesseg, korlatozva: e.savKorlatozva, indokok };
}

export function levezetes(
  eredmeny: BecslesEredmeny,
  alapterulet: number,
  jellemzok: Record<string, string>,
): Levezetes {
  if (eredmeny.tipus === 'szakertoi') {
    return {
      tipus: 'szakertoi',
      ok: eredmeny.ok,
      osszefoglalo: 'Ehhez az ingatlanhoz nem készült automatikus ár-sáv, a helyszíni szemle után adható pontos ár.',
      magyarazat: SZAKERTOI_MAGYARAZAT[eredmeny.ok] ?? 'Az ok nem ismert.',
    };
  }

  const lepesek: Lepes[] = [];
  let aktualis = eredmeny.alaparFtPerM2;
  const ksh = eredmeny.forras !== 'iroda';

  const alaparReszlet = [FORRAS_CIMKE[eredmeny.forras] ?? eredmeny.forras];
  if (eredmeny.alaparUgyletszam != null) alaparReszlet.push(`${eredmeny.alaparUgyletszam} adásvétel`);
  if (eredmeny.referenciaEv != null) alaparReszlet.push(`${eredmeny.referenciaEv}. évi adat`);
  lepesek.push({
    fajta: 'alapar', kulcs: 'alapar', cimke: ksh ? 'Környék KSH-alapára' : 'Az iroda alapára',
    reszlet: alaparReszlet.join(', '), szorzo: null, elotte: null, utana: aktualis, valtozasFt: 0, valtozasSzazalek: 0,
  });

  if (ksh) {
    const utana = eredmeny.alaparFtPerM2 * eredmeny.indexArany;
    const pontok = eredmeny.indexMostPontok;
    const referencia = eredmeny.indexReferencia != null ? `, a ${eredmeny.referenciaEv}. évi átlag ${szam(eredmeny.indexReferencia, 1)} pont` : null;
    const reszlet = pontok && pontok.length > 1 && referencia
      ? `MNB-lakásárindex: a ${[...pontok].reverse().map((p) => negyedevSzoveg(p.negyedev)).join(' és ')} átlaga ${szam(pontok.reduce((s, p) => s + p.index, 0) / pontok.length, 1)} pont (${[...pontok].reverse().map((p) => szam(p.index, 1)).join(' és ')})${referencia}. Szorzó: ${szam(eredmeny.indexArany)}.`
      : eredmeny.indexMost && referencia
        ? `MNB-lakásárindex: ${negyedevSzoveg(eredmeny.indexMost.negyedev)} ${szam(eredmeny.indexMost.index, 1)} pont${referencia}. Szorzó: ${szam(eredmeny.indexArany)}.`
        : `A KSH-adat idejétől a mai szintig az MNB-lakásárindex szerint. Szorzó: ${szam(eredmeny.indexArany)}.`;
    lepesek.push({
      fajta: 'index', kulcs: 'index', cimke: 'Vetítés a mai árszintre', reszlet,
      szorzo: eredmeny.indexArany, elotte: aktualis, utana, valtozasFt: utana - aktualis, valtozasSzazalek: (eredmeny.indexArany - 1) * 100,
    });
    aktualis = utana;
  }

  for (const sz of eredmeny.alkalmazottSzorzok) {
    const utana = aktualis * sz.szorzo;
    lepesek.push({
      fajta: 'szorzo', kulcs: sz.kulcs,
      cimke: `${ADAT_CIMKE[sz.kulcs] ?? sz.kulcs}: ${ertekCimke(sz.kulcs, sz.ertek)}`,
      reszlet: `Szorzó: ${szam(sz.szorzo)}${sz.forras === 'iroda' ? ', az iroda saját beállítása' : ''}`,
      szorzo: sz.szorzo, elotte: aktualis, utana, valtozasFt: utana - aktualis, valtozasSzazalek: (sz.szorzo - 1) * 100,
    });
    aktualis = utana;
  }

  const alkalmazott = new Set(eredmeny.alkalmazottSzorzok.map((s) => s.kulcs));
  const nemModosit = Object.entries(jellemzok)
    .filter(([kulcs]) => !alkalmazott.has(kulcs))
    .map(([kulcs, ertek]) => ({ cimke: ADAT_CIMKE[kulcs] ?? kulcs, ertek: ertekCimke(kulcs, ertek) }));

  const kozepNyers = aktualis * alapterulet;
  const lepesNev = kozepNyers < KEREKITES_HATAR ? '0,5 millió' : '1 millió';
  const osszefoglalo = ksh
    ? 'A környék KSH-adásvételi átlagára, mai árszintre vetítve, az ingatlan jellemzői szerint módosítva.'
    : 'Az iroda saját alapára, az ingatlan jellemzői szerint módosítva.';

  return {
    tipus: 'sav', osszefoglalo, lepesek, nemModosit,
    nyersFtPerM2: aktualis, ftPerM2: eredmeny.ftPerM2, alapterulet, kozepNyers,
    minNyers: kozepNyers * (1 - eredmeny.savSzelesseg), maxNyers: kozepNyers * (1 + eredmeny.savSzelesseg),
    sav: savLeiras(eredmeny), kerekitesSzoveg: `Kerekítve ${lepesNev} Ft-ra.`,
    min: eredmeny.min, kozep: eredmeny.kozep, max: eredmeny.max,
  };
}

export interface VizualisSor { balSzazalek: number; szelessegSzazalek: number }

/**
 * A lépcsős (waterfall) diagram geometriája: minden sáv a lépés előtti és utáni Ft/m² között, a közös,
 * nem nulláról induló skálán (különben a néhány százalékos szorzók láthatatlanok lennének).
 * Az alapár és a végső érték teljes sáv a skála aljától.
 */
export function lepcsoGeometria(lepesek: Lepes[], vegso: number): { lepesek: VizualisSor[]; vegso: VizualisSor; skalaMin: number; skalaMax: number } {
  const ertekek = [...lepesek.map((l) => l.utana), ...lepesek.flatMap((l) => (l.elotte === null ? [] : [l.elotte])), vegso];
  const also = Math.min(...ertekek);
  const felso = Math.max(...ertekek);
  const rahagyas = Math.max((felso - also) * 0.25, felso * 0.05, 1);
  const skalaMin = Math.max(0, also - rahagyas);
  const skalaMax = felso + rahagyas * 0.4;
  const span = skalaMax - skalaMin;
  const sz = (x: number) => Math.min(100, Math.max(0, ((x - skalaMin) / span) * 100));
  const sor = (a: number, b: number): VizualisSor => {
    const bal = sz(Math.min(a, b));
    return { balSzazalek: bal, szelessegSzazalek: Math.max(1, sz(Math.max(a, b)) - bal) };
  };
  return {
    lepesek: lepesek.map((l) => (l.elotte === null ? sor(skalaMin, l.utana) : sor(l.elotte, l.utana))),
    vegso: sor(skalaMin, vegso), skalaMin, skalaMax,
  };
}

function objektum(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

const SAV_SZAMMEZOK = ['min', 'kozep', 'max', 'ftPerM2', 'alaparFtPerM2', 'indexArany', 'savSzelesseg'] as const;

/**
 * A lead mentett jsonb-jéből (`becsles_reszletek`, `becsles_bemenet`) a levezetés. Ha bármi hiányzik vagy nem a várt alakú
 * (régi vagy kézzel javított lead), null: a panel ilyenkor el sem jelenik, de az adatlap többi része igen.
 */
export function levezetesLeadbol(lead: { becsles_reszletek: unknown; becsles_bemenet: unknown }): Levezetes | null {
  const e = objektum(lead.becsles_reszletek);
  if (!e || (e.tipus !== 'sav' && e.tipus !== 'szakertoi')) return null;
  const be = objektum(lead.becsles_bemenet);
  const alapterulet = typeof be?.alapterulet === 'number' && Number.isFinite(be.alapterulet) ? be.alapterulet : null;
  if (e.tipus === 'sav') {
    if (alapterulet === null || !Array.isArray(e.alkalmazottSzorzok)) return null;
    if (!SAV_SZAMMEZOK.every((k) => typeof e[k] === 'number' && Number.isFinite(e[k]))) return null;
    if (!e.alkalmazottSzorzok.every((sz) => typeof objektum(sz)?.szorzo === 'number' && Number.isFinite(objektum(sz)!.szorzo))) return null;
  }
  const jellemzok: Record<string, string> = {};
  for (const [k, v] of Object.entries(objektum(be?.jellemzok) ?? {})) if (typeof v === 'string') jellemzok[k] = v;
  try {
    return levezetes(e as unknown as BecslesEredmeny, alapterulet ?? 0, jellemzok);
  } catch (hiba) {
    // Szándékosan elnyelve: a levezetés csak magyarázat, egy váratlan alakú régi mentés miatt nem dőlhet el az adatlap.
    console.error('A becslés levezetése nem készíthető el', hiba);
    return null;
  }
}
